/**
 * firmaTaseronService.js — Dekoratör ⇄ Taşeron ilişkisi (`firma_taseron`), 9 uç.
 *
 * Kaynak: docs/guides/FE-FirmaTaseronController-guide.md (yeni controller, 2026-09-03).
 * Base: {JAVA_BACKEND_API_URL}/firma-taseron
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * MODEL: Bir DEKORATÖR bir TAŞERONU (zemin/temizlik/güvenlik/nakliye) bir FUAR için
 * tutar. 2026-09-03 öncesinde bu ilişki `firma_to_stand`'e, talebin açıldığı rastgele
 * standa çakılıyordu — yanlış bir cümle, çünkü dekoratörün zemin firması o dekoratörün
 * kurduğu HER standda çalışır. Artık DEKORATÖR + FUAR'a çapalanıyor.
 *
 *   Katılımcı birini tutar            → firma_to_stand (o stand için)
 *   Dekoratör bir DEKORATÖR tutar (3) → firma_to_stand (seviye-2 alt dekoratör, değişmedi)
 *   Dekoratör bir TAŞERON tutar (>=4) → firma_taseron (YENİ) + firma_to_fuar, STAND SATIRI YOK
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ AUTH FARKLI: bu controller'da YEREL BYPASS YOK — bu drop'taki tek controller.
 * Gerçek oturum şart: `X-user-Id` + `X-token` (getAuthHeader ikisini de gönderiyor).
 * Tokensiz çağrı **401 ve BOŞ GÖVDE** döner (yerelde doğrulandı 2026-09-04) — asla
 * JSON.parse etmeyin. ⚠️ Spring Security 6 gerçek bir 500'ü de 401 gibi giydirebiliyor,
 * yani buradaki 401 her zaman "oturum yok" demek değil.
 *
 * ⚠️ Mock katmanı YOK: iki onay kapısı yalnızca gerçek backend'e karşı anlamlı
 * (personnelAdminService.js / kisiAdminService.js ile aynı gerekçe).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * DEVİR KALKTI (backlog #381 backend / #382 frontend, 2026-10-13). Bu bağlantı artık yalnızca
 * "bu dekoratör bu fuarda bu taşeronla çalışıyor" KAYDI + iki tarafın onayıdır:
 *   • Kâğıt yok: iki kapı da hiçbir form yüklenmeden yanıtlanır. "Taşeron - Dekoratör İzin
 *     Formu" (91) ve "Dekoratör - Taşeron Onay Formu" (93) emekli; yükleme ucu `ilgiliFirmaId`'yi
 *     her tür için reddediyor.
 *   • Paylaşım yok: `personelGorunur` / `gorevlendirmeYetkisi` bayrakları kalktı, `PUT /paylasim`
 *     410 veriyor, taseron-karar gövdedeki bayrakları yok sayıp 0 yazıyor. Bir firma başka bir
 *     firma adına görevlendirme yapamaz; dekoratör taşeronun kadrosunu görmez (403).
 *   • DTO'daki `personelGorebilir`, `gorevlendirmeYapabilir`, `izinFormu*`, `onayFormu*`
 *     alanları hâlâ gelir (hep false/null) — ekranlar OKUMAZ.
 * Eski form + devir anlatısı git geçmişinde (#382 commit'inden önceki sürüm) ve
 * util/knowledgebase/personel-listesi-ve-taseron.md'nin 2026-10-13 notunda.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { getFirmaTypes } from './firmaAramaService';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/firma-taseron`;

// ─── Sabitler ─────────────────────────────────────────────────────────────────────────

/**
 * Kapıların KULLANICIYA GÖRÜNEN adları (backend `FirmaTaseronDurumConstant.kapiLabels`
 * ile birebir aynı metinler). "Kapı 1 / Kapı 2" bir daha ekrana yazılmaz — sunucu
 * mesajları da bu kelimeleri kullanıyor, iki dil bir ekranda görünmesin.
 * ⚠️ 1/2 sayıları teknik: `GET /{id}/gecmis` satırları `kapi` alanında bunları döner.
 */
export const KAPI = {
  TASERON_BEYANI: 1,
  DEKORATOR_ONAYI: 2,
};
export const KAPI_ADI = {
  get 1() { return i18n.t("services:firmaTaseronService.taseronFirmaBeyani"); },
  get 2() { return i18n.t("services:firmaTaseronService.dekoratorFirmaOnayi"); },
};

/**
 * `FirmaTaseronDurumConstant` — KAPI 1 (Taşeron Firma Beyanı): taşeronun kendi cevabı.
 * ⚠️ 3 (İptal) yalnızca `DELETE /{id}` tarafından yazılır, çağıran asla gönderemez.
 */
export const TASERON_DURUM = {
  0: { get label() { return i18n.t("services:firmaTaseronService.bekliyor"); }, color: 'warning', get aciklama() { return i18n.t("services:firmaTaseronService.taseronFirmayaHenuzUlasilmadiHicbir"); } },
  1: { get label() { return i18n.t("services:firmaTaseronService.onaylandi"); }, color: 'success', get aciklama() { return i18n.t("services:firmaTaseronService.taseronEvetOnlarinTaseronuyuzDedi"); } },
  2: { get label() { return i18n.t("services:firmaTaseronService.reddedildi"); }, color: 'danger', get aciklama() { return i18n.t("services:firmaTaseronService.taseronIliskiyiReddettiSatirSilinmez"); } },
  3: { get label() { return i18n.t("services:firmaTaseronService.iptal"); }, color: 'secondary', get aciklama() { return i18n.t("services:firmaTaseronService.onaylanmisIliskiSonlandirildi"); } },
};

/** KAPI 2 (Dekoratör Firma Onayı): dekoratörün teyidi. */
export const DEKORATOR_ONAY = {
  0: { get label() { return i18n.t("services:firmaTaseronService.bekliyor"); }, color: 'warning' },
  1: { get label() { return i18n.t("services:firmaTaseronService.onaylandi"); }, color: 'success' },
  2: { get label() { return i18n.t("services:firmaTaseronService.reddedildi"); }, color: 'danger' },
};

export const RED_SEBEBI_MAX = 500;
/** `not` (karar notu) ve geri almanın `sebep` alanı — ikisi de 500, ikisi de isteğe bağlı. */
export const NOT_MAX = 500;
export const SEBEP_MAX = 500;

/** `isTaseron(id)` backend'de `id >= 4` — açık uçlu, sabit bir liste DEĞİL. */
export const TASERON_MIN_FIRMA_TYPE_ID = 4;

/**
 * Taşeron hizmet tipi seçenekleri (4 Taşeron, 5 Nakliye, 6 Zemin, 7 Temizlik, 8 TEM, 14 Güvenlik…) —
 * `GET /firma/types`'tan, `{ firmaTypeId, label }`. Etiketler getFirmaTypes'ın çözdüğü adlardır, yani
 * Taşeron Bağlantıları sayfasındaki sütunla aynı sözlük; ikinci bir harita tutulmaz.
 * 15 (DenemeType) test satırıdır, gizlenir.
 */
export async function getTaseronTipleri() {
  const types = await getFirmaTypes({ forceLive: true });
  return (types || []).filter(
    (t) => Number(t.firmaTypeId) >= TASERON_MIN_FIRMA_TYPE_ID && Number(t.firmaTypeId) !== 15,
  );
}

/**
 * #750 — fuar formunun kendi tipleri: 7 Temizlik, 11 Proje Destek, 12 Operasyon Destek, 13 İSG Destek,
 * 14 Güvenlik, 16 Tedarikçi. Backend `FirmaFuarRolService.standRolu` bunları bilerek atlar:
 * `POST /standTalep/assignFirma` firmayı standa bağlar ama firma_to_fuar rolü YAZMAZ ve kullanıcılarına
 * fuarı vermez. Bu yüzden iki stand seçicisinde (Stand Detayı › Firmalar, Personel Ekle › Firmayı Fuara
 * Eşle › "Bir standda çalışıyor") bu tipler gizlenir; yalnızca fuar formundan eklenirler.
 */
export const FUAR_FORMU_FIRMA_TYPE_IDS = [7, 11, 12, 13, 14, 16];

/** Standa bağlanabilen taşeron türleri — getTaseronTipleri() eksi fuar formunun tipleri (#750). */
export async function getStandTaseronTipleri() {
  const list = await getTaseronTipleri();
  return list.filter((t) => !FUAR_FORMU_FIRMA_TYPE_IDS.includes(Number(t.firmaTypeId)));
}

// ─── Ortak yardımcılar ────────────────────────────────────────────────────────────────

/**
 * Controller'ın hata haritası tek tip: IllegalArgument→400, Security→403,
 * IllegalState→409, diğer→500. Gövde `{message, result:false, data:null}` ve mesaj
 * Türkçe — olduğu gibi gösterilebilir.
 * ⚠️ 401'in gövdesi BOŞ.
 */
function parseError(error) {
  const status = error.response?.status ?? null;
  let message =
    typeof error.response?.data === 'string' && error.response.data
      ? error.response.data
      : error.response?.data?.message ?? error.message ?? i18n.t("services:firmaTaseronService.bilinmeyenHata");
  if (status === 401) {
    message =
      i18n.t("services:firmaTaseronService.oturumDogrulanamadiBuEkranGercek") +
      i18n.t("services:firmaTaseronService.yenidenGirisYapmayiDeneyin");
  }
  const wrapped = new Error(message);
  wrapped.status = status;
  throw wrapped;
}

/** 409 — "zaten yanıtlanmış / zaten aktif bağlantı var / artık aktif değil". */
export function isConflict(error) {
  return error?.status === 409;
}

/** 403 — "bu tarafın kararı sizde değil". */
export function isForbidden(error) {
  return error?.status === 403;
}

// ─── Okumalar ─────────────────────────────────────────────────────────────────────────
// Dördü de DÜZ DİZİ döner: zarf yok, sayfalama yok, toplam yok. Sıra `id DESC`.
// Sayfalama gerekiyorsa istemci tarafında yapın (rehber "Blocker 6").

/**
 * "Altımda kim çalışıyor" — dekoratörün kendi ekranı, bir fuardaki maiyeti.
 * `durum` VERİLMEZSE her durum döner; bu ekranın istediği de budur.
 * GET /api/v1/firma-taseron/ana/{anaFirmaId}?fuarId=&durum=&sadeceAktif=
 */
export async function getTaseronlarim(anaFirmaId, { fuarId, durum, sadeceAktif = true } = {}) {
  try {
    const params = { fuarId, sadeceAktif };
    if (durum !== undefined && durum !== null) params.durum = durum;
    const response = await axios.get(`${BASE}/ana/${anaFirmaId}`, { params, headers: getAuthHeader() });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * "Kim beni taşeronu gösteriyor" — taşeronun kendi ekranı.
 * ⚠️ Burada KASITLI olarak `durum` filtresi YOK: bu ekran bekleyenleri yanıtlamak için
 * var, yanlışlıkla filtrelenip gözden kaçmamalı.
 * GET /api/v1/firma-taseron/taseron/{taseronFirmaId}?fuarId=&sadeceAktif=
 */
export async function getBeniTaseronGosterenler(taseronFirmaId, { fuarId, sadeceAktif = true } = {}) {
  try {
    const response = await axios.get(`${BASE}/taseron/${taseronFirmaId}`, {
      params: { fuarId, sadeceAktif },
      headers: getAuthHeader(),
    });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * SAKA kuyruğu — bir fuardaki tüm bağlantılar. Yalnızca personel; firma hesabı hangi
 * id'yi geçerse geçsin 403 alır.
 * ⚠️ `fuarId` VERİLMEZSE VERİTABANINDAKİ TÜM BAĞLANTILAR döner — her zaman gönderin.
 * `durum=0` işe yarayan çağrı: taşeronu aramamızı bekleyen her şey.
 * GET /api/v1/firma-taseron/fuar?fuarId=&durum=&sadeceAktif=
 */
export async function getFuarTaseronBaglantilari({ fuarId, durum, sadeceAktif = true } = {}) {
  if (!fuarId) throw new Error(i18n.t("services:firmaTaseronService.fuarIdZorunludurVerilmezseBackendTum"));
  try {
    const params = { fuarId, sadeceAktif };
    if (durum !== undefined && durum !== null) params.durum = durum;
    const response = await axios.get(`${BASE}/fuar`, { params, headers: getAuthHeader() });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * Tek bağlantı — iki taraf da ve personel okuyabilir.
 * ⚠️ Bilinmeyen id **400** döner, 404 değil (controller IllegalArgument'ı 400'e çeviriyor).
 * GET /api/v1/firma-taseron/{id}
 */
export async function getTaseronBaglantisi(id) {
  try {
    const response = await axios.get(`${BASE}/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

// ─── Yazmalar ─────────────────────────────────────────────────────────────────────────

/**
 * Personelin doğrudan bağlantı açması — telefonla halledilen durum, arkasında bir
 * `firma_sent_firmas` talebi yok. YALNIZCA PERSONEL, bilerek: talep-ve-onay yolunu
 * atladığı için firmaya açık olsa firma kendine yetki verirdi.
 *
 * Evrak atlanınca ONAY ATLANMAZ: satır iki kapı da KAPALI doğar
 * (`durum 0`, `dekoratorOnay 0`, iki paylaşım bayrağı da false).
 * Yan etkisi var: taşeronu `firma_to_fuar`'a ve TÜM kullanıcılarını `user_to_fuar`'a
 * ekler — yoksa taşeron giriş yapıp hiçbir şey göremez ve kapı 1'i kimse yanıtlayamaz.
 *
 * ⚠️ REDDEDİLMİŞ BİR BAĞLANTI DA YENİSİNİ ENGELLER: mükerrer kontrolü yalnızca
 * `aktif = 1`'e bakıyor, `durum`a bakmıyor. Bu yüzden reddedilmiş satırın yanında
 * "bağlantıyı sonlandır" düğmesi ŞART, yoksa kuyruk 409'da kilitlenir.
 * POST /api/v1/firma-taseron
 */
export async function createTaseronBaglantisi({ fuarId, anaFirmaId, taseronFirmaId, taseronFirmaTypeId } = {}) {
  try {
    const body = { fuarId, anaFirmaId, taseronFirmaId };
    if (taseronFirmaTypeId) body.taseronFirmaTypeId = taseronFirmaTypeId;
    const response = await axios.post(BASE, body, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * KAPI 1 (Taşeron Firma Beyanı) — taşeron "evet, onların taşeronuyuz" ya da "hayır" der.
 *
 * ⚠️ Yanıtlandıktan sonra doğrudan tekrar yanıtlanamaz (409); yanlış cevabın çıkışı
 * `geriAlTaseronKarar` + yeniden cevap.
 *
 * ⚠️ `onayla` GÖNDERİLMEZSE `false` okunur — kimsenin göndermediği izin, kimsenin
 * vermediği izindir. Yani boş gövde bir RED denemesidir ve eksik gerekçeden patlar.
 * İmzalı form ARTIK ŞART DEĞİL (#381) ve iki paylaşım bayrağı kalktı (gövdedekini
 * sunucu yok sayıp 0 yazar), bu yüzden onay gövdesi yalnızca `{ onayla: true }`.
 *
 * `not` — serbest metin karar notu (en fazla 500). Onayda da rette de gönderilebilir;
 * `redSebebi`nin yerine geçmez, onun yanında durur. ⚠️ Tel üzerindeki alan adı
 * gerçekten `not`, `karar_notu` değil.
 * PUT /api/v1/firma-taseron/{id}/taseron-karar
 */
export async function taseronKarar(id, { onayla, redSebebi, not } = {}) {
  try {
    const body = onayla ? { onayla: true } : { onayla: false, redSebebi };
    if (not && not.trim()) body.not = not.trim();
    const response = await axios.put(`${BASE}/${id}/taseron-karar`, body, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * KAPI 2 (Dekoratör Firma Onayı) — dekoratör teyit eder. Kapı 1'den farkı:
 * kapı 1'DEN ÖNCE yanıtlanabilir (aramalar gerçekten çakışıyor), satır `durum 0` +
 * `dekoratorOnay 1` durumunda oturabilir.
 *
 * ⚠️ 2026-09-19'a kadar "bir ANAHTAR"dı: yalnızca `aktif = 1` kontrol ediliyor, mevcut
 * değer hiç okunmuyordu — yani her yeni cevap bir öncekini İZ BIRAKMADAN eziyordu.
 * #67 bunu kapattı: FUAR FİİLEN SÜRERKEN yanıtlanmış bir kapı 2 doğrudan
 * değiştirilemez (409), önce `geriAlDekoratorKarar` ile geri alınır.
 * ⚠️ BU DURUM İSTEMCİDE ÖNGÖRÜLEMEZ: "fuar sürüyor mu" `fuar_setup_startdate` /
 * `fuar_enddate` ile hesaplanıyor, BİLİNMEYEN pencere CANLI sayılıyor (73 fuarın
 * 26'sında tarih yok) ve DTO'da böyle bir alan yok. Dolayısıyla ekran tahmin
 * ETMEMELİ: yanıtlanmış bir kapı 2 için tek yol "geri al, sonra yeniden cevapla".
 *
 * İmzalı form ARTIK ŞART DEĞİL (#381). `not` alanı kapı 1'deki ile aynı.
 *
 * Bu ayrı bir onaydır: taşeronu talep etmek, onunla çalıştığını teyit etmekle aynı şey değil.
 * PUT /api/v1/firma-taseron/{id}/dekorator-karar
 */
export async function dekoratorKarar(id, { onayla, redSebebi, not } = {}) {
  try {
    const body = onayla ? { onayla: true } : { onayla: false, redSebebi };
    if (not && not.trim()) body.not = not.trim();
    const response = await axios.put(`${BASE}/${id}/dekorator-karar`, body, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * KAPI 1'i geri al — yanlış verilmiş bir beyanı silmez, ESKİSİNİ TARİHÇEYE YAZIP
 * satırı "Bekliyor"a döndürür. Gövde tamamen isteğe bağlı; `sebep` de öyle
 * (en fazla 500, kapı kararlarındaki `redSebebi`nin aksine zorunlu DEĞİL).
 *
 * (Eskiden iki paylaşım bayrağını da sıfırlardı; bayraklar #381 ile kalktığı için
 * ekranın bunu ayrıca söylemesine gerek yok.)
 * ⚠️ Yetki: yalnızca TAŞERON firma ya da personel (403). Dekoratör kendi lehine
 * verilmiş bir beyanı geri alamaz.
 * PUT /api/v1/firma-taseron/{id}/taseron-karar/geri-al
 */
export async function geriAlTaseronKarar(id, { sebep } = {}) {
  try {
    const body = sebep && sebep.trim() ? { sebep: sebep.trim() } : {};
    const response = await axios.put(`${BASE}/${id}/taseron-karar/geri-al`, body, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * KAPI 2'yi geri al — kapı 1'in aksine hiçbir bayrağa dokunmaz, yalnızca onayı
 * "Bekliyor"a çeker ve eskisini tarihçeye yazar. Fuar sürerken de çalışır; zaten
 * doğrudan üzerine yazmanın 409 verdiği durumda ÇIKIŞ YOLU budur.
 * ⚠️ Yetki: yalnızca TALEP EDEN firma (dekoratör) ya da personel (403).
 * PUT /api/v1/firma-taseron/{id}/dekorator-karar/geri-al
 */
export async function geriAlDekoratorKarar(id, { sebep } = {}) {
  try {
    const body = sebep && sebep.trim() ? { sebep: sebep.trim() } : {};
    const response = await axios.put(`${BASE}/${id}/dekorator-karar/geri-al`, body, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Bir bağlantının karar tarihçesi — ESKİYEN cevaplar, en yenisi başta.
 * Yalnızca bir cevap BAŞKA bir cevapla değiştirildiğinde satır yazılır (geri alma ya
 * da üzerine yazma); ilk cevabın kendisi tarihçeye girmez, o satırın üstünde durur.
 *
 * ⚠️ BOŞ TARİHÇE "bir kez cevaplandı" DEMEK DEĞİL — "2026-09-19'dan bu yana hiçbir şey
 * eskimedi" demek. Tablo o gün doğdu ve kapı 2 ondan önce kendini sessizce eziyordu.
 * Ekran bunu bağlantının tam geçmişi gibi sunmamalı.
 * ⚠️ Zarf yok, TİPLİ DTO da yok: düz nesne dizisi. `eskiPersonelGorunur` /
 * `eskiGorevlendirmeYetkisi` NULL olabilir — kapı 2 satırlarında "uygulanmaz"
 * demektir, "hayır" değil.
 * GET /api/v1/firma-taseron/{id}/gecmis
 */
export async function getKararGecmisi(id) {
  try {
    const response = await axios.get(`${BASE}/${id}/gecmis`, { headers: getAuthHeader() });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * İlişkiyi sonlandır — fiil DELETE ama satır SİLİNMEZ: `aktif = 0` olur, onaylıysa
 * ayrıca `durum = 3` (İptal). Güncellenmiş nesneyi döner, böylece satır listeden
 * düşmek yerine yeniden çizilebilir.
 * Görevlendirme anında durur; ÖNCEDEN yazılmış `person_firma_to_fuar` satırları KALIR.
 * DELETE /api/v1/firma-taseron/{id}
 */
export async function endTaseronBaglantisi(id) {
  try {
    const response = await axios.delete(`${BASE}/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}
